import { z } from 'zod';
import { DocumentCategorySchema, DocumentCategoryGroupSchema } from './documents.schemas';
import { refersTo } from '../common/refers-to';

/**
 * Property data-room readiness (Verkaufsreife) — IMM-383.1. The read side of the
 * readiness cockpit: per property type, which documents should be on file and
 * whether each is present / missing / expiring / expired, rolled up into a
 * single advisory signal. Tokens are en-US code register; German display labels
 * live in `@immo/i18n`.
 */

// The ten readiness profiles: the Objekt's type + residential Untertyp, paired
// with its marketing type (IMM-792.5). Only ETW/EFH/MFH/GEW have a letting variant.
export const READINESS_PROFILE_VALUES = [
  'ETW',
  'EFH',
  'MFH',
  'GST',
  'GEW',
  'GAR',
  'ETW_RENTAL',
  'EFH_RENTAL',
  'MFH_RENTAL',
  'GEW_RENTAL',
] as const;
export const ReadinessProfileSchema = z.enum(READINESS_PROFILE_VALUES);
export type ReadinessProfile = z.infer<typeof ReadinessProfileSchema>;

// Overall advisory signal — Verkaufsreif / Fast vollständig / Unvollständig; a missing optional row never lowers it (IMM-1470).
export const READINESS_SIGNAL_VALUES = ['SALE_READY', 'ALMOST_COMPLETE', 'INCOMPLETE'] as const;
export const ReadinessSignalSchema = z.enum(READINESS_SIGNAL_VALUES);
export type ReadinessSignal = z.infer<typeof ReadinessSignalSchema>;

// Whether a row blocks Verkaufsreif — Pflicht / Optional.
export const READINESS_OBLIGATION_VALUES = ['MANDATORY', 'OPTIONAL'] as const;
export const ReadinessObligationSchema = z.enum(READINESS_OBLIGATION_VALUES);
export type ReadinessObligation = z.infer<typeof ReadinessObligationSchema>;

// Row state — Vorhanden / Fehlt / Läuft ab / Abgelaufen.
export const READINESS_STATUS_VALUES = ['PRESENT', 'MISSING', 'EXPIRING', 'EXPIRED'] as const;
export const ReadinessStatusSchema = z.enum(READINESS_STATUS_VALUES);
export type ReadinessStatus = z.infer<typeof ReadinessStatusSchema>;

/**
 * The Eigentümer an Anforderung is recorded against — IMM-749. Surfaced so the
 * request dialog can name the recipient („Empfänger" block, IMM-383 mock 03).
 * Null when the Objekt has no Eigentümer contact or that contact has no e-mail
 * on file: the two states the request action guards against.
 */
export const ReadinessOwnerSchema = z.object({
  // The Eigentümer contact the request e-mail is addressed to, so the composer
  // can greet them and Vorlagen can resolve against them.
  contactId: refersTo('contact', z.uuid()),
  name: z.string().nullable(),
  email: z.email(),
});
export type ReadinessOwner = z.infer<typeof ReadinessOwnerSchema>;

export const ReadinessItemSchema = z.object({
  category: DocumentCategorySchema,
  group: DocumentCategoryGroupSchema,
  obligation: ReadinessObligationSchema,
  status: ReadinessStatusSchema,
  // A conditional row carries the „nur wenn zutreffend" hint (Erbbaurecht,
  // Denkmalschutz). Never blocking; shown where the trait can occur.
  conditional: z.boolean(),
  // The document on file satisfying the row, if any; null when MISSING.
  documentId: refersTo('document', z.uuid()).nullable(),
  // Extracted validity date (ISO), set on EXPIRING/EXPIRED; null otherwise.
  validUntil: z.string().nullable(),
  // IMM-383.2 — when the Kategorie was last requested from the owner (ISO), or
  // null if never / already filed. Drives the „Angefordert am {Datum}" chip.
  lastRequestedAt: z.string().nullable(),
});
export type ReadinessItem = z.infer<typeof ReadinessItemSchema>;

export const PropertyReadinessResponseSchema = z.object({
  profile: ReadinessProfileSchema,
  signal: ReadinessSignalSchema,
  // Mandatory rows satisfied (PRESENT or EXPIRING) vs. total mandatory rows —
  // the "{X} von {Y} Pflichtdokumenten vorhanden" counter.
  mandatoryPresentCount: z.number().int().nonnegative(),
  mandatoryRequiredCount: z.number().int().nonnegative(),
  // IMM-1470 — every checklist row, Pflicht and Optional: rows satisfied (PRESENT or
  // EXPIRING) vs. all rows — the "{X} von {Y} Unterlagen vorhanden" counter.
  presentCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  // IMM-383.2 / IMM-749 — the Objekt's Eigentümer contact and its e-mail, or
  // null when either is missing; null disables the owner-request action with a
  // plain reason, and non-null names the recipient in the request dialog.
  owner: ReadinessOwnerSchema.nullable(),
  // Mandatory rows first, then optional; each block in DOCUMENT_GROUP_ORDER.
  items: z.array(ReadinessItemSchema),
});
export type PropertyReadinessResponse = z.infer<typeof PropertyReadinessResponseSchema>;
